import { Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import * as Time from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import { ModalActionButtons } from "@outschool/ui-legacy-component-library";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import * as Env from "../../../shared/Env";
import {
  ActivityEnrollQueryQuery,
  EnrollmentMeetingPurchaseQueryQuery,
  SendMessageToUserMutation,
  SendMessageToUserMutationVariables,
} from "../../../shared/generated/graphql";
import * as Routes from "../../../shared/Routes";
import { useUserMode } from "../../lib/UserMode";
import ErrorMessage from "../ErrorMessage";
// @ts-ignore - JSX import
import { sendMessageMutation } from "../MessageModal";

type RequestAgeRangeExceptionProps = {
  activity: // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
  | ActivityEnrollQueryQuery["enrollmentForm"]["activity"]
    // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
    | EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["activity"];
  // @ts-ignore TS(2339): Property 'section' does not exist on type '{ __typ... Remove this comment to see the full error message
  section?: ActivityEnrollQueryQuery["enrollmentForm"]["section"];
  onSend: (event: React.MouseEvent) => void;
  onCancel: (event: React.MouseEvent) => void;
  startTime?: Date;
  endTime?: Date;
  learner: any;
};

function composeSectionContent(
  activity: // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
  | ActivityEnrollQueryQuery["enrollmentForm"]["activity"]
    // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
    | EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["activity"],
  // @ts-ignore TS(2339): Property 'section' does not exist on type '{ __typ... Remove this comment to see the full error message
  section: ActivityEnrollQueryQuery["enrollmentForm"]["section"],
  note: string,
  userTimeZone: string,
): string {
  const sectionName = Time.fullDurationStringWithStartTime(
    section.start_time,
    section.end_time,
    activity.leader.timeZone,
  );
  return `${note}

------
Enrollment requested into section: ${sectionName}
Learner's time zone: ${Time.timeZoneName(userTimeZone)}

Teachers can approve age range exceptions by sending the family a private section link to enroll into, and then transferring the learner into the requested section.
`;
}

function composeActivityContent(
  // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
  activity: EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["activity"],
  startTime: Date,
  endTime: Date,
  note: string,
  userTimeZone: string,
): string {
  const requestedTime = Time.fullDurationStringWithStartTime(
    startTime,
    endTime,
    activity.leader.timeZone,
  );

  return `${note}

------
Enrollment requested for: ${requestedTime}
Learner's time zone: ${Time.timeZoneName(userTimeZone)}

Teachers can approve age range exceptions by sending the family a private link to enroll with.
`;
}

export default function RequestAgeRangeException({
  activity,
  section,
  startTime,
  endTime,
  learner,
  onCancel,
  onSend,
}: RequestAgeRangeExceptionProps) {
  const { t } = useTranslation(
    "client\\components\\account\\RequestAgeRangeException",
  );
  const userMode = useUserMode();
  const { userTimeZone } = useSession();
  const { setTopNotice } = useTopNoticeContext();
  const noteInputRef = useRef<HTMLTextAreaElement>(null);
  const [error, setError] = useState("");

  const learnerAge = learner?.age;
  const noteDefaultText = learnerAge
    ? t(
        "I’d like to request an age range exception for my {{learnerAge}}-year-old to enroll in this class.\n\nReason for request:",
        { learnerAge },
      )
    : t(
        "I’d like to request an age range exception for my learner to enroll in this class.\n\nReason for request:",
      );
  const [note, setNote] = useState(noteDefaultText);

  const [sendPrivateMessage] = useMutation<
    SendMessageToUserMutation,
    SendMessageToUserMutationVariables
  >(sendMessageMutation);
  const trackEvent = useTrackEvent();

  const handleSubmit = async (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    const content = !!section
      ? // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        composeSectionContent(activity, section, note, userTimeZone)
      : composeActivityContent(
          activity,
          // @ts-ignore TS(2345): Argument of type 'Date | undefined' is not assigna... Remove this comment to see the full error message
          startTime,
          endTime,
          note,
          userTimeZone,
        );
    try {
      const {
        // @ts-ignore TS(2339): Property 'sendMessage' does not exist on type 'Sen... Remove this comment to see the full error message
        data: { sendMessage: message },
      } = await sendPrivateMessage({
        variables: {
          recipientUids: [activity.leader.uid],
          messageInput: {
            content,
            activityUid: activity.uid,
            sectionUid: section?.uid,
            details: {
              action: "message during enrollment",
              activityTitle: activity.title,
              subject: t(
                "Requesting Age Range Exception for {{learner.name}} into {{activity.title}}",
                { activity, learner },
              ),
            },
          },
        },
      });
      trackEvent("Age range exception requested", {
        activityUid: activity.uid,
        sectionUid: section?.uid,
      });
      onSend(event);
      setTopNotice(
        <>
          <span>{t`Your request was sent.`}</span>{" "}
          {message.thread_uid && (
            <span>
              <Trans t={t}>
                View your conversation{" "}
                <Link
                  to={Routes.websiteRoutes.adultThreadPathV2(
                    userMode,
                    message.thread_uid,
                  )}
                >
                  here
                </Link>
                .
              </Trans>
            </span>
          )}
        </>,
      );
      close();
    } catch (e) {
      setError(e);
    }
  };
  return (
    <>
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
      <Typography variant="h3">{t`Request Age Range Exception:`}</Typography>
      <textarea
        value={note}
        onChange={e => {
          setNote(e.target.value);
          // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
          setError(null);
        }}
        name="noteInput"
        ref={noteInputRef}
        rows={5}
        style={{
          width: "100%",
          boxSizing: "border-box",
          marginBottom: "0.5em",
          height: "auto",
        }}
      ></textarea>
      <div>
        {/* eslint-disable react/no-unescaped-entities */}
        <Trans t={t}>
          <p>
            Note: Age range exceptions are at the educator’s discretion, and
            your request may not be accepted.
          </p>
          <p>
            If the educator accepts your request, they will reply with a link
            for you to enroll.
          </p>
        </Trans>
        {/* eslint-enable react/no-unescaped-entities */}
      </div>
      <ModalActionButtons
        cancelLabel={t("Back")}
        onCancel={onCancel}
        primaryLabel={t`Request Exception`}
        primaryType="submit"
        buttonColor="secondary"
        onPrimary={handleSubmit}
      />
    </>
  );
}
